import { useLayoutEffect, type ReactNode } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import { cleverDarkTheme, cleverLightTheme } from '../../theme/cleverThemes';

// Self-hosted brand fonts (SIL OFL) — bundled by the app build, no Google Fonts hotlink
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/alexandria/400.css';
import '@fontsource/alexandria/500.css';
import '@fontsource/alexandria/600.css';
import '@fontsource/alexandria/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/700.css';
import '../../brand.css';

/**
 * Backstage switches the MUI theme but does NOT set the [data-theme-mode]
 * attribute that @backstage/ui keys its CSS token layer off. Without this the
 * BUI tokens stay on their light values under the dark theme, so BUI-rendered
 * surfaces render light-on-dark. Bind the attribute to the active variant.
 */
const BuiMode = ({
  mode,
  children,
}: {
  mode: 'light' | 'dark';
  children?: ReactNode;
}) => {
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme-mode', mode);
  }, [mode]);
  return <>{children}</>;
};

// Named 'light'/'dark' so these replace the built-in app themes wholesale
const cleverLight = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Clever Light',
      variant: 'light',
      icon: <WbSunnyIcon />,
      Provider: ({ children }) => (
        <BuiMode mode="light">
          <UnifiedThemeProvider theme={cleverLightTheme} children={children} />
        </BuiMode>
      ),
    },
  },
});

const cleverDark = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Clever Dark',
      variant: 'dark',
      icon: <Brightness2Icon />,
      Provider: ({ children }) => (
        <BuiMode mode="dark">
          <UnifiedThemeProvider theme={cleverDarkTheme} children={children} />
        </BuiMode>
      ),
    },
  },
});

export const cleverThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [cleverLight, cleverDark],
});
